import { useEffect } from "react";
// mine:
import { GameManager, useGameManager } from "@/app/lib/game-state/game-manager";
import { GameMode } from "@/app/lib/game-state/game-types";
import UI, { useUI } from "@/app/lib/ui/ui";
import { Path } from "@/app/lib/routing/path"
import { useInitialLoad } from "./initial-load";
import { useTutorialCheck } from "@/app/lib/setup/tutorialCheck";
import { useAutoSave } from "@/app/lib/setup/autosave";
import { useTouchCheck } from "@/app/lib/setup/touch-check";
import { useVictoryCheck } from "@/app/lib/setup/victory-check";
import { fullStory } from "@/app/lib/levels/full-story";
import { useTheme } from "@/app/lib/setup/theme";
import { useAutoFocus } from "@/app/lib/setup/autoFocus";
import { useUrl } from "@/app/lib/setup/url";

export default function useSetup(
    initialPath: Path | null,
    debugMode: boolean,
    gameVersions: Record<GameMode, string>,
    puzzleDictionaryName: string,
    solutionsDictionaryName: string
) : {
    gameManager: GameManager,
    ui: UI
} {
    // game state management
    const gameManager: GameManager = useGameManager({
        gameVersions,
        puzzleDictionaryName,
        solutionsDictionaryName,
        fullStory
    });
    // build our ui
    const ui: UI = useUI();

    // EFFECTS
    //--------------------------------
    // INITIAL LOAD
    useInitialLoad(initialPath, gameManager, ui);

    // TUTORIAL CHECK
    // check if we've not visited before and need to do a tutorial
    useTutorialCheck(gameManager, ui);

    // AUTO SAVE -- FOR PUZZLES AND STREAK
    useAutoSave(gameManager, ui);

    // MOBILE CHECK
    useTouchCheck(ui, false);

    // CHECK FOR VICTORY / COMPLETED
    useVictoryCheck(gameManager, ui);

    // AUTOFOCUS (Handles initial letterbox focus and listens for autofocus request signals)
    useAutoFocus(gameManager, ui);

    // Toggles themes between pyramid/story
    useTheme(gameManager);

    // Maintains the appearance of URL navigation
    useUrl(gameManager, ui);

    // Set debug mode
    useEffect(() => {
        ui.setDebugMode(debugMode);
    }, []);
    
    return {
        gameManager,
        ui
    }
}